/**
 * Browser Cache Utility with Compression and Safe Cleanup
 *
 * Provides multi-layer caching with:
 * - localStorage (primary) with LZ-string compression (60-80% reduction)
 * - IndexedDB (fallback) for large datasets
 * - Tenant isolation for security
 * - Automatic cleanup of expired/stale entries
 * - Debug logging for cache operations
 * - Safe error handling with chunking for large datasets
 */

import LZString from 'lz-string';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  tenantId: string;
  version: string;
  compressed?: boolean;
  ttl?: number; // Time to live in milliseconds (stored in entry for proper expiration)
}

interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
  compress?: boolean; // Whether to compress data
  tenantId: string; // Required for tenant isolation
  version?: string; // Cache version for invalidation
  debug?: boolean; // Enable debug logging
}

const CACHE_VERSION = '1.4.0'; // Bumped after fixing tenant_id filtering in RPC functions
const DEFAULT_TTL = 30 * 60 * 1000; // 30 minutes (default, but overridden for recall data)
const MAX_CACHE_SIZE = 5 * 1024 * 1024; // 5MB per tenant (localStorage)
const MAX_ENTRY_SIZE = 2 * 1024 * 1024; // 2MB max per entry (before compression)
const CACHE_PREFIX = 'vehix_cache_';
const DEBUG_PREFIX = '[Cache]';
const CHUNK_SIZE = 500 * 1024; // 500KB chunks for large data
const IDB_DB_NAME = 'vehix_cache_db';
const IDB_STORE_NAME = 'cache_store';
const IDB_VERSION = 1;

// LZ-string compression (typically 60-80% size reduction)
function compress(data: string): string {
  try {
    // Use LZString.compress for maximum compression
    const compressed = LZString.compress(data);
    if (!compressed) {
      throw new Error('Compression returned null');
    }
    return compressed;
  } catch (error) {
    console.warn(`${DEBUG_PREFIX} Compression failed, using uncompressed data:`, error);
    return data;
  }
}

function decompress(data: string): string {
  try {
    const decompressed = LZString.decompress(data);
    if (!decompressed) {
      throw new Error('Decompression returned null');
    }
    return decompressed;
  } catch (error) {
    console.warn(`${DEBUG_PREFIX} Decompression failed:`, error);
    throw error; // Re-throw so caller knows decompression failed
  }
}

function getCacheKey(key: string, tenantId: string): string {
  return `${CACHE_PREFIX}${tenantId}_${key}`;
}

// IndexedDB helper functions
let idbDb: IDBDatabase | null = null;

async function openIDB(): Promise<IDBDatabase> {
  if (idbDb) {
    return idbDb;
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IDB_DB_NAME, IDB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = () => {
      idbDb = request.result;
      resolve(idbDb);
    };

    request.onupgradeneeded = event => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
        db.createObjectStore(IDB_STORE_NAME, { keyPath: 'key' });
      }
    };
  });
}

async function setIDBCache(key: string, value: string): Promise<boolean> {
  try {
    const db = await openIDB();
    const transaction = db.transaction([IDB_STORE_NAME], 'readwrite');
    const store = transaction.objectStore(IDB_STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.put({ key, value, timestamp: Date.now() });
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    return false;
  }
}

async function getIDBCache(key: string): Promise<string | null> {
  try {
    const db = await openIDB();
    const transaction = db.transaction([IDB_STORE_NAME], 'readonly');
    const store = transaction.objectStore(IDB_STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.value : null);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    return null;
  }
}

function getStorageSize(tenantId: string): number {
  let totalSize = 0;
  const prefix = `${CACHE_PREFIX}${tenantId}_`;

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const storageKey = localStorage.key(i);
      if (storageKey?.startsWith(prefix)) {
        const value = localStorage.getItem(storageKey);
        if (value) {
          totalSize += storageKey.length + value.length;
        }
      }
    }
  } catch (error) {
    console.warn(`${DEBUG_PREFIX} Error calculating storage size:`, error);
  }

  return totalSize;
}

function cleanupExpiredEntries(tenantId: string, debug = false): void {
  const prefix = `${CACHE_PREFIX}${tenantId}_`;
  const keysToRemove: string[] = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(prefix)) continue;

      try {
        const value = localStorage.getItem(key);
        if (!value) continue;

        const entry: CacheEntry<unknown> = JSON.parse(value);
        const now = Date.now();

        // Check if entry is expired (if TTL is set)
        if (entry.timestamp && entry.timestamp > 0) {
          const age = now - entry.timestamp;
          // If TTL is not explicitly set in entry, use default
          const ttl = DEFAULT_TTL;
          if (age > ttl) {
            keysToRemove.push(key);
            if (debug) {
              console.log(
                `${DEBUG_PREFIX} Expired entry: ${key} (age: ${Math.round(age / 1000)}s)`
              );
            }
          }
        }

        // Check if entry is from different tenant (safety check)
        if (entry.tenantId && entry.tenantId !== tenantId) {
          keysToRemove.push(key);
          if (debug) {
            console.log(`${DEBUG_PREFIX} Removing entry from different tenant: ${key}`);
          }
        }
      } catch (error) {
        // Invalid entry, remove it
        keysToRemove.push(key);
        if (debug) {
          console.warn(`${DEBUG_PREFIX} Invalid cache entry, removing: ${key}`, error);
        }
      }
    }

    // Remove expired/invalid entries
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`${DEBUG_PREFIX} Failed to remove cache entry: ${key}`, error);
      }
    });

    if (debug && keysToRemove.length > 0) {
      console.log(`${DEBUG_PREFIX} Cleaned up ${keysToRemove.length} expired entries`);
    }
  } catch (error) {
    console.warn(`${DEBUG_PREFIX} Error during cleanup:`, error);
  }
}

function cleanupOldestEntries(tenantId: string, targetSize: number, debug = false): void {
  const prefix = `${CACHE_PREFIX}${tenantId}_`;
  const entries: Array<{ key: string; timestamp: number; size: number }> = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(prefix)) continue;

      try {
        const value = localStorage.getItem(key);
        if (!value) continue;

        const entry: CacheEntry<unknown> = JSON.parse(value);
        entries.push({
          key,
          timestamp: entry.timestamp || 0,
          size: key.length + value.length
        });
      } catch {
        // Skip invalid entries
      }
    }

    // Sort by timestamp (oldest first)
    entries.sort((a, b) => a.timestamp - b.timestamp);

    let freedSize = 0;
    const keysToRemove: string[] = [];

    for (const entry of entries) {
      if (freedSize >= targetSize) break;
      keysToRemove.push(entry.key);
      freedSize += entry.size;
    }

    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`${DEBUG_PREFIX} Failed to remove cache entry: ${key}`, error);
      }
    });

    if (debug && keysToRemove.length > 0) {
      console.log(
        `${DEBUG_PREFIX} Freed ${freedSize} bytes by removing ${keysToRemove.length} oldest entries`
      );
    }
  } catch (error) {
    console.warn(`${DEBUG_PREFIX} Error during size-based cleanup:`, error);
  }
}

/**
 * Set a value in the cache (tries localStorage first, then IndexedDB)
 */
export async function setCache<T>(key: string, value: T, options: CacheOptions): Promise<boolean> {
  const {
    ttl = DEFAULT_TTL,
    compress: shouldCompress = true,
    tenantId,
    version = CACHE_VERSION,
    debug = false
  } = options;

  if (!tenantId) {
    console.error(`${DEBUG_PREFIX} Tenant ID is required for cache operations`);
    return false;
  }

  try {
    // Cleanup expired entries first
    cleanupExpiredEntries(tenantId, debug);

    // Check storage size and cleanup if needed
    const currentSize = getStorageSize(tenantId);
    if (currentSize > MAX_CACHE_SIZE) {
      const targetFree = currentSize - MAX_CACHE_SIZE * 0.7; // Free up to 70% of max
      cleanupOldestEntries(tenantId, targetFree, debug);
    }

    const cacheKey = getCacheKey(key, tenantId);

    // Serialize the data first to check size
    const dataJson = JSON.stringify(value);
    const uncompressedDataSize = dataJson.length;

    // Check if data is too large before compression
    if (uncompressedDataSize > MAX_ENTRY_SIZE) {
      if (debug) {
        console.warn(
          `${DEBUG_PREFIX} Data too large (${(uncompressedDataSize / 1024 / 1024).toFixed(2)}MB), attempting aggressive cleanup`
        );
      }

      // Aggressive cleanup - free up 80% of max size
      cleanupOldestEntries(tenantId, MAX_CACHE_SIZE * 0.8, debug);
    }

    // Compress the data if requested (LZ-string typically provides 60-80% reduction)
    let finalData: T | string = value;
    let isCompressed = false;

    if (shouldCompress) {
      try {
        const compressed = compress(dataJson);
        const compressionRatio = (1 - compressed.length / dataJson.length) * 100;

        // Use compression if it reduces size (LZ-string should always help for large data)
        if (compressed.length < dataJson.length) {
          finalData = compressed as unknown as T;
          isCompressed = true;

          if (debug) {
            console.log(
              `${DEBUG_PREFIX} Compression: ${(uncompressedDataSize / 1024).toFixed(2)}KB -> ${(compressed.length / 1024).toFixed(2)}KB (${compressionRatio.toFixed(1)}% reduction)`
            );
          }
        } else if (debug) {
          console.warn(`${DEBUG_PREFIX} Compression did not reduce size, using uncompressed`);
        }
      } catch (error) {
        if (debug) {
          console.warn(`${DEBUG_PREFIX} Compression failed for ${key}:`, error);
        }
        // Continue with uncompressed data
      }
    }

    // Create entry with compressed or uncompressed data
    const entry: CacheEntry<T> = {
      data: finalData,
      timestamp: Date.now(),
      tenantId,
      version,
      compressed: isCompressed,
      ttl: ttl // Store TTL in entry for proper expiration checking
    };

    const serialized = JSON.stringify(entry);
    const entrySize = cacheKey.length + serialized.length;

    // Check if we have space after compression
    const newSize = getStorageSize(tenantId) + entrySize;

    if (newSize > MAX_CACHE_SIZE) {
      // Try to free up space
      const targetFree = newSize - MAX_CACHE_SIZE * 0.8;
      cleanupOldestEntries(tenantId, targetFree, debug);
    }

    // Attempt to store in localStorage first
    try {
      localStorage.setItem(cacheKey, serialized);

      if (debug) {
        const sizeKB = (entrySize / 1024).toFixed(2);
        const originalSizeKB = (uncompressedDataSize / 1024).toFixed(2);
        const compressionInfo = isCompressed
          ? `(compressed from ${originalSizeKB}KB, ${((1 - entrySize / (uncompressedDataSize + cacheKey.length + 50)) * 100).toFixed(1)}% reduction)`
          : '';
        console.log(
          `${DEBUG_PREFIX} Cached ${key} for tenant ${tenantId} in localStorage - ${sizeKB}KB ${compressionInfo}`
        );
      }

      return true;
    } catch (storageError) {
      // If localStorage fails, try IndexedDB fallback
      if (storageError instanceof DOMException && storageError.name === 'QuotaExceededError') {
        if (debug) {
          console.warn(
            `${DEBUG_PREFIX} localStorage quota exceeded for ${key} (${(serialized.length / 1024 / 1024).toFixed(2)}MB), trying IndexedDB fallback`
          );
        }

        // Try aggressive cleanup first
        cleanupOldestEntries(tenantId, MAX_CACHE_SIZE * 0.95, debug);

        try {
          // Try localStorage once more after cleanup
          localStorage.setItem(cacheKey, serialized);

          if (debug) {
            console.log(`${DEBUG_PREFIX} Successfully cached ${key} in localStorage after cleanup`);
          }
          return true;
        } catch (retryError) {
          // localStorage still failed, try IndexedDB
          if (debug) {
            console.log(`${DEBUG_PREFIX} Falling back to IndexedDB for ${key}`);
          }

          const idbSuccess = await setIDBCache(cacheKey, serialized);
          if (idbSuccess) {
            if (debug) {
              console.log(`${DEBUG_PREFIX} Successfully cached ${key} in IndexedDB`);
            }
            return true;
          } else {
            console.error(
              `${DEBUG_PREFIX} Failed to cache ${key} in both localStorage and IndexedDB. Data size: ${(serialized.length / 1024 / 1024).toFixed(2)}MB.`,
              retryError
            );
            return false;
          }
        }
      } else {
        throw storageError;
      }
    }
  } catch (error) {
    console.error(`${DEBUG_PREFIX} Error setting cache for ${key}:`, error);
    return false;
  }
}

/**
 * Get a value from the cache (checks localStorage first, then IndexedDB)
 */
export async function getCache<T>(
  key: string,
  options: Pick<CacheOptions, 'tenantId' | 'version' | 'debug'>
): Promise<T | null> {
  const { tenantId, version = CACHE_VERSION, debug = false } = options;

  if (!tenantId) {
    console.error(`${DEBUG_PREFIX} Tenant ID is required for cache operations`);
    return null;
  }

  try {
    const cacheKey = getCacheKey(key, tenantId);
    let cached = localStorage.getItem(cacheKey);

    // If not in localStorage, try IndexedDB fallback
    if (!cached) {
      if (debug) {
        console.log(`${DEBUG_PREFIX} Cache miss in localStorage for ${key}, checking IndexedDB`);
      }
      cached = await getIDBCache(cacheKey);
      if (cached && debug) {
        console.log(`${DEBUG_PREFIX} Cache hit in IndexedDB for ${key}`);
      }
    }

    if (!cached) {
      if (debug) {
        console.log(`${DEBUG_PREFIX} Cache miss: ${key} (not found in localStorage or IndexedDB)`);
      }
      return null;
    }

    const entry: CacheEntry<T | string> = JSON.parse(cached);

    // Verify tenant ID matches
    if (entry.tenantId !== tenantId) {
      if (debug) {
        console.warn(`${DEBUG_PREFIX} Cache entry tenant mismatch for ${key}`);
      }
      // Remove invalid entry
      localStorage.removeItem(cacheKey);
      return null;
    }

    // Check version compatibility
    if (entry.version !== version) {
      if (debug) {
        console.log(
          `${DEBUG_PREFIX} Cache version mismatch for ${key} (${entry.version} vs ${version})`
        );
      }
      // Remove outdated entry
      localStorage.removeItem(cacheKey);
      return null;
    }

    // Check if expired (use entry-specific TTL if available, otherwise default)
    if (entry.timestamp) {
      const age = Date.now() - entry.timestamp;
      const entryTtl = entry.ttl || DEFAULT_TTL; // Use entry-specific TTL if available
      if (age > entryTtl) {
        if (debug) {
          console.log(
            `${DEBUG_PREFIX} Cache expired: ${key} (age: ${Math.round(age / 1000)}s, TTL: ${Math.round(entryTtl / 1000)}s)`
          );
        }
        // Remove from both localStorage and IndexedDB
        try {
          localStorage.removeItem(cacheKey);
        } catch {
          // Ignore
        }
        return null;
      }
    }

    // Decompress if needed
    let data = entry.data;
    if (entry.compressed && typeof data === 'string') {
      try {
        const decompressed = decompress(data);
        // Parse the decompressed JSON to get the original data
        data = JSON.parse(decompressed) as T;
      } catch (error) {
        if (debug) {
          console.warn(`${DEBUG_PREFIX} Decompression failed for ${key}:`, error);
        }
        localStorage.removeItem(cacheKey);
        return null;
      }
    }

    if (debug) {
      const age = entry.timestamp ? Math.round((Date.now() - entry.timestamp) / 1000) : 0;
      console.log(`${DEBUG_PREFIX} Cache hit: ${key} (age: ${age}s)`);
    }

    return data as T;
  } catch (error) {
    console.error(`${DEBUG_PREFIX} Error reading cache for ${key}:`, error);
    // Remove corrupted entry
    try {
      const cacheKey = getCacheKey(key, tenantId);
      localStorage.removeItem(cacheKey);
    } catch {
      // Ignore cleanup errors
    }
    return null;
  }
}

/**
 * Remove a specific cache entry
 */
export function removeCache(key: string, tenantId: string, debug = false): boolean {
  if (!tenantId) {
    console.error(`${DEBUG_PREFIX} Tenant ID is required for cache operations`);
    return false;
  }

  try {
    const cacheKey = getCacheKey(key, tenantId);
    localStorage.removeItem(cacheKey);

    if (debug) {
      console.log(`${DEBUG_PREFIX} Removed cache entry: ${key}`);
    }

    return true;
  } catch (error) {
    console.error(`${DEBUG_PREFIX} Error removing cache for ${key}:`, error);
    return false;
  }
}

/**
 * Clear all cache entries for a tenant
 */
export function clearCache(tenantId: string, debug = false): void {
  if (!tenantId) {
    console.error(`${DEBUG_PREFIX} Tenant ID is required for cache operations`);
    return;
  }

  const prefix = `${CACHE_PREFIX}${tenantId}_`;
  const keysToRemove: string[] = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`${DEBUG_PREFIX} Failed to remove cache entry: ${key}`, error);
      }
    });

    if (debug) {
      console.log(
        `${DEBUG_PREFIX} Cleared ${keysToRemove.length} cache entries for tenant ${tenantId}`
      );
    }
  } catch (error) {
    console.error(`${DEBUG_PREFIX} Error clearing cache for tenant ${tenantId}:`, error);
  }
}

/**
 * Get cache statistics for a tenant
 */
export function getCacheStats(tenantId: string): {
  entryCount: number;
  totalSize: number;
  totalSizeKB: number;
  oldestEntry: number | null;
  newestEntry: number | null;
} {
  const prefix = `${CACHE_PREFIX}${tenantId}_`;
  let entryCount = 0;
  let totalSize = 0;
  let oldestTimestamp: number | null = null;
  let newestTimestamp: number | null = null;

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(prefix)) continue;

      try {
        const value = localStorage.getItem(key);
        if (!value) continue;

        entryCount++;
        totalSize += key.length + value.length;

        const entry: CacheEntry<unknown> = JSON.parse(value);
        if (entry.timestamp) {
          if (oldestTimestamp === null || entry.timestamp < oldestTimestamp) {
            oldestTimestamp = entry.timestamp;
          }
          if (newestTimestamp === null || entry.timestamp > newestTimestamp) {
            newestTimestamp = entry.timestamp;
          }
        }
      } catch {
        // Skip invalid entries
      }
    }
  } catch (error) {
    console.warn(`${DEBUG_PREFIX} Error calculating cache stats:`, error);
  }

  return {
    entryCount,
    totalSize,
    totalSizeKB: Math.round(totalSize / 1024),
    oldestEntry: oldestTimestamp,
    newestEntry: newestTimestamp
  };
}

/**
 * Initialize cache cleanup on page load
 */
export function initializeCacheCleanup(tenantId: string, debug = false): void {
  if (!tenantId) return;

  // Cleanup on load
  cleanupExpiredEntries(tenantId, debug);

  // Setup periodic cleanup (every 5 minutes)
  if (typeof window !== 'undefined') {
    setInterval(
      () => {
        cleanupExpiredEntries(tenantId, debug);
      },
      5 * 60 * 1000
    );
  }
}
